import { AreaDict } from './index';

/**
 * Investor Portal (src/components/InvestorPortal.tsx) translations.
 *
 * Only strings NOT already covered by LanguageContext's base keys live here —
 * base keys win on merge, so duplicating one would be dead weight. Prefix is
 * `ip` so this area can never collide with tenant/guest/PM/document dictionaries.
 */
export const investorDict: AreaDict = {
  en: {
    // ── Shell / navigation ────────────────────────────────────────────────
    ipPartner: 'Partner',
    ipUser: 'User',
    ipPropertyMgmtShort: 'Property Mgmt',
    ipFeed: 'Feed',
    // Four-tab shell. The old bar carried Dashboard / Property Mgmt / Feed /
    // Properties / Portfolio, three of which meant the same thing to an owner:
    // my properties. These are the four that are actually different.
    ipNavHome: 'Home',
    ipNavConsole: 'Console',
    ipNavProperties: 'Properties',
    ipNavPortfolio: 'Portfolio',
    ipTabReport: 'Report',
    ipTabManagement: 'Property Management',
    ipTabValuation: 'Valuation',
    // Corporate: trade licences, visas and transactions from the CRM.
    ipTabCorporate: 'Corporate',
    // InvestMenter Brain: DLD vs the agency deal history vs the AI.
    ipTabBrain: 'Asset Desk AI',
    imbWorking: 'Asset Desk AI is working for you',
    imbTaskPlans: 'Loading your payment plans',
    imbTaskValuation: 'Valuing your portfolio with DLD data',
    imbTaskPortfolio: 'Loading your portfolio',
    imbTaskDealHistory: 'Reading Asset Desk deal history',
    imbPage1Eyebrow: '01 · Public data',
    imbPage1Title: 'Anyone can compare DLD data.',
    imbPage1Lead: 'The Dubai Land Department publishes every sale in Dubai. Any website and any AI can read it. Here is your portfolio against it.',
    imbPaid: 'Paid',
    imbDld: 'DLD value',
    imbDiff: 'Difference',
    imbNoUnits: 'No properties with a purchase price yet.',
    imbDldLoading: 'Loading the DLD valuation…',
    imbDldNone: 'The DLD valuation is not available right now.',
    imbNoDld: 'Too few sales',
    imbComps: '{n} comparable sales',
    imbConfLow: 'low confidence',
    imbMoreUnits: '+ {n} more properties in Valuation',
    imbDldSource: 'Source: Dubai Land Department transactions · last update {date}',
    imbDldSourceNoDate: 'Source: Dubai Land Department transactions',
    imbNext: 'Next',
    imbBack: 'Back',
    imbPage2Eyebrow: '02 · Only Asset Desk',
    imbPage2Title: 'Only Asset Desk can add its own deal history.',
    imbPage2Lead: 'Not everyone can add the data gathered by the biggest German-speaking real estate agency: what our clients actually paid, when, in which projects, and how their resales turned out. DLD does not publish it. No one else has it.',
    imbHistLoading: 'Loading the deal history…',
    imbHistNone: 'No project has enough client deals to show yet.',
    imbHistError: 'The deal history could not be loaded right now.',
    imbHistAsOf: 'Shown as of {date}; the latest update could not be loaded.',
    imbRetry: 'Try again',
    imbDeals: 'client deals',
    imbClients: 'clients',
    imbProjects: 'projects',
    imbBought: 'bought',
    imbSince: 'since {year}',
    imbResaleAll: 'resales: median {roi}% a year',
    imbYouVsClients: 'You vs other Asset Desk clients',
    imbDealsBy: '{deals} deals · {clients} clients',
    imbYouPaid: 'You paid',
    imbClientsMedian: 'Clients’ median',
    imbPerSqft: '/sqft',
    imbTopProjects: 'Where our clients buy',
    imbResale: 'resales {roi}% a year',
    imbPrivacy: 'Anonymised: no names, no unit numbers. A project appears only when {n} or more different clients hold a deal in it, and price per sqft is rounded to AED 50.',
    imbPage3Eyebrow: '03 · The Asset Desk AI',
    imbPage3Kicker: 'A normal AI cannot do this.',
    imbPage3Title: 'The Asset Desk AI can.',
    imbPage3Lead: 'Ask ChatGPT and you get the same as everyone else: public data, and only what you prompt it with yourself. The Asset Desk AI reads the DLD data, our own deal history and your portfolio together, and answers with all three.',
    imbAskTitle: 'Ask it',
    imbPage4Eyebrow: '04 · The main question',
    imbPage4Kicker: 'Are you a real estate expert?',
    imbPage4Title: 'Or are you really good at what you do yourself?',
    imbPage4Lead: 'A good doctor, lawyer or software developer should do that 100%, and get the best knowledge from people who already do real estate every day and night.',
    imbMissTitle: 'Without Asset Desk AI you miss',
    imbMiss1: 'What other investors really paid in your projects, not only what DLD publishes.',
    imbMiss2: 'How resales in those projects actually turned out.',
    imbMiss3: 'An AI that answers with this knowledge, instead of what everyone else gets.',
    imbClose: 'Be smart. Use the knowledge of Asset Desk AI, your AI Wealth Co-Pilot.',
    imbCta: 'Ask Asset Desk AI',
    imbCtaMessage: 'I would like to find the right Dubai investment for me.',
    imbAsk1: 'Did I pay a good price in {project}, against DLD and against other Asset Desk clients?',
    imbAsk1Generic: 'Did I pay a good price, against DLD and against other Asset Desk clients?',
    imbAsk2: 'Which of my properties has gained the most, by DLD value?',
    imbAsk3: 'How have Asset Desk clients done when they resold?',
    ipNavMore: 'More',
    // Money sub-tabs: what comes in, what goes out, what is still owed.
    ipMoneyPlans: 'Plans',
    // Properties hub: the two portfolio-wide reports, reached from the list
    // rather than sitting beside it as rival tabs.
    ipHubReport: 'Portfolio report',
    ipHubValuations: 'Valuations',
    ipHubBackToList: 'Back to properties',
    ipMoreTools: 'Calculators & tools',
    // Personal documents on the profile. They belong to the person, not to a
    // building, which is why they are here and not in the Properties screen.
    ipPersonalDocsSub: 'Passport, visa and Emirates ID',
    ipDownload: 'Download',
    ipDocument: 'Document',
    ipProcessingDocumentAI: 'Processing document with AI...',
    ipChangeRole: 'Change role (Owner / Tenant / Guest)',
    ipAboutUs: 'About Us',

    // ── Auth: shared form chrome ──────────────────────────────────────────
    ipSignIn: 'Sign In',
    ipCreateAccount: 'Create Account',
    ipUsername: 'Username',
    ipPassword: 'Password',
    ipForgotEmail: 'Forgot Email?',
    ipForgotPassword: 'Forgot Password?',
    ipEnterYourEmail: 'Enter your email',
    ipEnterYourPhone: 'Enter your phone number',
    ipEnterYourPassword: 'Enter your password',
    ipEnterFullName: 'Enter your full name',
    ipSelectNationality: 'Select nationality',
    ipOptionalParen: '(optional)',
    ipCannotBeChanged: '(cannot be changed)',
    ipHidePassword: 'Hide password',
    ipShowPassword: 'Show password',
    ipPrivateClientAccess: 'PRIVATE CLIENT ACCESS',
    ipJoinInvestMenter: 'Join Asset Desk',
    ipAccessPremiumPortfolio: 'Access your premium investment portfolio',
    ipCreatePremiumAccount: 'Create your premium investor account',
    ipNoAccountSignUp: "Don't have an account? Sign up",
    ipHaveAccountSignIn: 'Already have an account? Sign in',
    ipEnterEmailFirst: 'Enter your email first',
    ipSignInWithFaceRecognition: 'Sign in with Face Recognition',
    ipAgeAndAgree: 'I am 18 or older and agree to the',
    ipTermsOfService: 'Terms of Service',
    ipAnd: 'and',
    ipPrivacyPolicy: 'Privacy Policy',
    ipConfirmAction: 'Confirm Action',
    ipYes: 'Yes',
    ipNo: 'No',
    ipBackToLogin: 'Back to Login',

    // ── Auth: account lockout ─────────────────────────────────────────────
    ipAccountLocked: 'Account Locked',
    ipAccountLockedDesc: 'Your account has been locked due to too many failed login attempts.',
    ipResetPasswordToUnlock: 'Reset Password to Unlock',
    ipWarningPrefix: 'Warning:',
    ipOneAttempt: '1 attempt',
    ipAttemptsPlural: 'attempts',
    ipBeforeLockoutSuffix: 'remaining before account lockout.',

    // ── Auth: messages ────────────────────────────────────────────────────
    ipEmailNotVerified: 'Your email address is not verified yet. Please check your inbox and click the verification link.',
    ipInvalidEmailOrPassword: 'Invalid email or password',
    ipLoginFailed: 'Login failed. Please try again.',
    ipEnterPhoneNumber: 'Please enter your phone number',
    ipFailedToSendCode: 'Failed to send code',
    ipConnectionError: 'Connection error. Please try again.',
    ipEnterVerificationCode: 'Please enter the verification code',
    ipEnterFull6DigitCode: 'Please enter the full 6-digit code',
    ipInvalidCode: 'Invalid code',
    ipVerificationFailed: 'Verification failed. Please try again.',
    ipEnter6DigitCode: 'Please enter a 6-digit code',
    ipInvalidVerificationCodeRetry: 'Invalid verification code. Please try again.',
    ipInvalidVerificationCode: 'Invalid verification code.',
    ipPasswordsDoNotMatch: 'Passwords do not match',
    ipFillAllRequiredFields: 'Please fill in all required fields',
    ipAcceptTermsToContinue: 'Please accept the Terms of Service and Privacy Policy to continue.',
    ipSignupFailed: 'Signup failed. Please try again.',
    ipSignupFailedConnection: 'Signup failed. Please check your connection and try again.',
    ipAccountCreatedPendingDocs: 'Account created! Please check your email and click the verification link. Your documents will be automatically linked to your account after verification.',
    ipAccountCreatedVerify: 'Account created! Please check your email and click the verification link, then sign in here.',
    ipUaePassFailed: 'UAE Pass authentication failed. Please try again.',
    ipEnterEmailAddress: 'Please enter your email address',
    ipFailedSendResetCode: 'Failed to send reset code. Please try again.',
    ipNetworkErrorRetry: 'Network error. Please check your connection and try again.',
    ipNetworkError: 'Network error. Please try again.',
    ipFillBothPasswordFields: 'Please fill in both password fields',
    ipFillPhoneAndNationality: 'Please fill in both phone number and nationality',
    ipPasswordMin6: 'Password must be at least 6 characters',
    ipPasswordResetSuccess: 'Password has been reset successfully! You can now login with your new password.',
    ipFailedResetPassword: 'Failed to reset password. Please try again.',
    ipRecoveredEmailIs: 'Your email address is:',
    ipRecoveredEmailNote: "For security reasons, we've partially hidden your email. A full recovery link has been sent to your registered phone number.",
    ipNoAccountForPhoneNationality: 'No account found with this phone number and nationality combination.',

    // ── Auth: two-factor ──────────────────────────────────────────────────
    ipTwoFactorAuthTitle: 'Two-Factor Authentication',
    ipSetUpTwoFactorAuth: 'Set Up Two-Factor Authentication',
    ipEnhanceSecurityWith2fa: 'Enhance your account security with 2FA',
    ipEnter6DigitCodeLabel: 'Enter the 6-digit code from your authenticator app',
    ipEnter6DigitFromAuthenticator: 'Please enter the 6-digit code from your authenticator app',
    ipAuthenticatorApp: 'Authenticator App',
    ipBackupCode: 'Backup Code',
    ipAuthenticationCode: 'Authentication Code',
    ipEnterBackupCode: 'Enter backup code',
    ipOpenAuthenticatorApp: 'Open your authenticator app to view your authentication code',
    ipTrustDevice30Days: 'Trust this device for 30 days',
    ipVerifying: 'Verifying...',
    ipVerifyAndSignIn: 'Verify & Sign In',
    ipLostAuthenticatorAccess: 'Lost access to your authenticator?',
    ipContactSupportRecovery: 'Please contact support at support@investmenter.com for account recovery assistance.',
    ipContactSupport: 'Contact Support',
    ipGenerating: 'Generating...',
    ipGenerateQrCode: 'Generate QR Code',
    ipStep1ScanQr: 'Step 1: Scan QR Code',
    ipBackupCodesCopied: 'Backup codes copied to clipboard!',
    ipEnabling: 'Enabling...',
    ipEnable2fa: 'Enable 2FA',
    ipDisable2fa: 'Disable 2FA',
    ipRegenerateBackupCodes: 'Regenerate Backup Codes',
    ipYourBackupCodes: 'Your Backup Codes',
    ipSkip2faConfirm: 'Are you sure you want to skip 2FA setup? You can enable it later in your security settings.',
    ipSkipForNow: 'Skip for now',
    ipFailedSetup2fa: 'Failed to setup 2FA. Please try again.',
    ipFailedEnable2fa: 'Failed to enable 2FA. Please try again.',
    ipFailedDisable2fa: 'Failed to disable 2FA. Please try again.',
    ipFailedDisable2faCheckCode: 'Failed to disable 2FA. Check your code and try again.',
    ipTwoFaEnabledAlert: 'Two-factor authentication has been successfully enabled!',
    ipTwoFaDisabledAlert: 'Two-factor authentication has been disabled.',
    ipInvalidCodeCheckAuthenticator: 'Invalid code. Please check your authenticator app.',
    ipEnterCodeToDisable2fa: 'Enter your current authenticator code to disable 2FA:',
    ipBackupCodesShownOnce: 'Backup codes are shown only once when 2FA is first set up. To get new codes, regenerate them.',
    ipRegenerateBackupCodesConfirm: 'Regenerate backup codes? Your old codes will no longer work.',
    ipNewBackupCodesGenerated: 'New backup codes generated. Save them now — they will not be shown again.',
    ipFailedRegenerateBackupCodes: 'Failed to regenerate backup codes.',

    // ── Auth: WhatsApp / SMS sign-in ──────────────────────────────────────
    ipSignInWithWhatsApp: 'Sign in with WhatsApp',
    ipEnterVerificationCodeTitle: 'Enter Verification Code',
    ipEnterRegisteredPhoneWhatsApp: 'Enter your registered phone number to receive a WhatsApp code',
    ipWeSentWhatsAppCodeTo: 'We sent a WhatsApp code to',
    ipSending: 'Sending...',
    ipSendCode: 'Send Code',
    ipResendCode: 'Resend Code',
    ipEnter6DigitCodePlaceholder: 'Enter 6-digit code',

    // ── Auth: password reset / email recovery ──────────────────────────────
    ipResetPassword: 'Reset Password',
    ipEnterYourNewPassword: 'Enter your new password',
    ipEnterCodeSentToEmail: 'Enter the verification code sent to your email',
    ipEnterEmailForResetCode: 'Enter your email address to receive a password reset code',
    ipEnterRegisteredEmail: 'Enter your registered email',
    ipVerificationCodeLabel: 'Verification Code',
    ipCheckEmailForCode: 'Check your email for the verification code',
    ipNewCodeSent: 'A new code has been sent to your email.',
    ipFailedResendCode: 'Failed to resend code.',
    ipDidntReceiveResend: "Didn't receive it? Resend Code",
    ipConfirmNewPassword: 'Confirm New Password',
    ipConfirmNewPasswordPlaceholder: 'Confirm new password',
    ipProcessing: 'Processing...',
    ipVerifyCode: 'Verify Code',
    ipSendResetCode: 'Send Reset Code',
    ipRecoverEmailAddress: 'Recover Email Address',
    ipRecoverEmailDesc: 'Enter your phone number and nationality to recover your email address',
    ipEnterRegisteredPhone: 'Enter your registered phone number',
    ipYourEmailAddress: 'Your email address',
    ipSearching: 'Searching...',
    ipFindMyEmail: 'Find My Email',

    // ── Auth: face / biometric modal ──────────────────────────────────────
    ipBiometricVerification: 'BIOMETRIC VERIFICATION',
    ipFaceAuthentication: 'Face Authentication',
    ipPositionFaceCentre: 'Position your face in the centre of the camera',
    ipAuthenticating: 'Authenticating...',
    ipAuthenticate: 'Authenticate',
    ipFaceDataEncryptedSecure: 'Your face data is encrypted and secure',
    ipEnrollYourFace: 'Enroll Your Face',
    ipCapturing: 'Capturing...',
    ipCaptureFace: 'Capture Face',

    // ── Documents: upload feedback ────────────────────────────────────────
    ipDuplicateDetected: '[X] Duplicate Document Detected',
    ipDuplicateCannotUploadA: 'Cannot upload: A',
    ipDuplicateCannotUploadB: 'document already exists for this unit.',
    ipDocumentLabel: 'Document:',
    ipUploadedOnLabel: 'Uploaded on:',
    ipDuplicateTipA: 'Tip: Please delete the existing',
    ipDuplicateTipB: 'document first if you want to upload a new one.',
    ipUploadFailedTitle: '[X] Upload Failed',
    ipDeleteExistingFirst: 'Please delete the existing document first.',
    ipErrorLabel: 'Error:',
    ipUnknownErrorOccurred: 'Unknown error occurred',
    ipUnknownError: 'Unknown error',
    ipTryAgainOrContactSupport: 'Please try again or contact support if the issue persists.',
    ipTryAgainOrContactSupportShort: 'Please try again or contact support.',
    ipUploadSuccessTitle: '[OK] Upload Successful!',
    ipTypeLabel: 'Type:',
    ipCategoryLabel: 'Category:',
    ipAiExtractedData: 'The AI has extracted the property data.',
    ipTeamWillVerify: 'Our team will verify the data before updating your property information.',
    ipStatusChangeOnceVerified: "You'll see the status change once verified.",
    ipDocUploadedProcessed: 'The document has been uploaded and processed successfully.',
    ipProcessingErrorTitle: '[X] Processing Error',
    ipProcessingErrorBody: 'An error occurred while processing the document:',
    ipLoginToDownload: 'Please log in to download documents.',
    ipCouldNotDownload: 'Could not download document. Please try again.',
    ipLoginToView: 'Please log in to view documents.',
    ipCouldNotOpen: 'Could not open document. Please try again.',
    ipSelectUnitFirstAlert: 'Please select a unit or add a new unit first',
    ipFileTooLarge: 'File too large. Please select a file smaller than 100MB.',
    ipMax20Files: 'Maximum 20 files at once. Please select fewer files.',
    ipFileTooLargeNamedPre: 'File "',
    ipFileTooLargeNamedPost: '" is too large (max 100MB). Please remove it and try again.',
    ipPreparingFiles: 'Preparing files...',
    ipReading: 'Reading',
    ipAiAnalyzing: 'AI is analyzing your documents...',
    ipDocsUploadedSuccessfully: 'document(s) uploaded successfully!',
    ipDocumentsDetected: 'Documents detected:',
    ipUnmatchedAdminWillAssign: '(unmatched - admin will assign)',
    ipPendingAdminVerification: 'All documents are pending admin verification.\nThey will appear in your app once verified by our team.',
    ipFilesFailed: 'file(s) failed:',
    ipUploadFailedLabel: 'Upload failed:',
    ipUploadErrorLabel: 'Upload error:',
    ipPleaseTryAgain: 'Please try again',
    ipFillRequiredUnitFields: 'Please fill in required fields: Name, Unit Number, and Project',
    ipSessionExpired: 'Session expired. Please login again.',
    ipUnitAddedPre: '[OK] Unit "',
    ipUnitAddedPost: '" added successfully! You can now upload documents.',
    ipUnitCreationFailed: 'Unit creation failed. Please try again.',
    ipFailedToCreateUnit: 'Failed to create unit:',

    // ── AI assistant ──────────────────────────────────────────────────────
    ipAiDefaultGreeting: "I'm here to help! How can I assist you with your Dubai real estate investment?",
    ipAiErrorMessage: "Sorry, I couldn't process your request. Please try again or use WhatsApp support for immediate help.",
    ipTryAsking: 'Try asking:',
    ipChatSuggestion1: "What's the current Dubai property market trend?",
    ipChatSuggestion2: 'How can I maximize my rental yield?',
    ipChatSuggestion3: 'What documents do I need for property investment?',
    ipChatSuggestion4: 'Tell me about golden visa through property',
    ipChatOnWhatsApp: 'Chat on WhatsApp',
    ipOnline: 'Online',
    ipSupportTeam: 'Support Team',
    ipBusinessHours: 'Available Mon-Fri, 9AM-6PM (Dubai Time)',

    // ── Profile ───────────────────────────────────────────────────────────
    ipProfileUpdated: 'Profile updated successfully!',
    ipFailedUpdateProfile: 'Failed to update profile. Please try again.',
    ipFillAllPasswordFields: 'Please fill in all password fields',
    ipCurrentPasswordIncorrect: 'Current password is incorrect',
    ipNewPasswordMin6: 'New password must be at least 6 characters',
    ipNewPasswordsDoNotMatch: 'New passwords do not match',
    ipFailed: 'Failed',
    ipPasswordChanged: 'Password changed successfully!',
    ipFailedChangePassword: 'Failed to change password. Please try again.',
    ipFailedDeleteAccount: 'Failed to delete account',
    ipFailedDeleteAccountRetry: 'Failed to delete account. Please try again.',
    ipSaving: 'Saving...',
    ipSaveChanges: 'Save Changes',
    ipUpdating: 'Updating...',
    ipUpdatePassword: 'Update Password',
    ipAccountStatistics: 'Account Statistics',
    ipAccountActivitySummary: 'Your account activity summary',
    ipDeleteAccount: 'Delete Account',
    ipDeleteAccountDesc: 'Permanently delete your account and all associated data. This action cannot be undone.',
    ipDeleteMyAccount: 'Delete My Account',
    ipDeleteAccountWarning: 'Warning: This will permanently delete your account',
    ipDeleteBullet1: 'All your personal data will be removed',
    ipDeleteBullet2: 'Your documents and portfolio data will be deleted',
    ipDeleteBullet3: 'This action cannot be reversed',
    ipTypeToConfirmPre: 'Type',
    ipTypeToConfirmPost: 'to confirm:',
    ipTypeDeleteToConfirm: 'Type DELETE to confirm',
    ipDeleting: 'Deleting...',
    ipPermanentlyDeleteAccount: 'Permanently Delete Account',

    // ── Dashboard (projections) ───────────────────────────────────────────
    ipInvestmentGrowth: 'Investment Growth',
    ipPortfolioValueLabel: 'Portfolio Value',
    ipInvest: 'Invest',
    ipAllTimeReturns: 'All time returns',
    ipTotalRentalIncome: 'Total rental income',
    ipAnnualIncome: 'Annual income',
    ipTotalAppreciation: 'Total appreciation',
    ipFromPurchaseToToday: 'From purchase date to today',
    ipGrowthRate: 'Growth Rate',
    ipPurchaseValueColon: 'Purchase Value:',
    ipCurrentValueColon: 'Current Value:',
    ipCapitalGainColon: 'Capital Gain:',
    ipCurrentColon: 'Current:',
    ipPropertyValueYear: 'Property Value Y',
    ipGrowthSuffix: 'growth',
    ipCumulativeRentalYear: 'Cumulative Rental Y',
    ipTotalRentCollectedShort: 'Total rent collected',
    ipTotalReturnYear: 'Total Return Y',
    ipCapitalPlusRental: 'Capital + Rental',
    ipPropertyValueGrowthChart: 'Property Value Growth (15% Annual Appreciation)',
    ipCumulativeRentalChart: 'Cumulative Rental Income (3% Annual Growth)',
    ipStartingMonthlyRental: 'Starting Monthly Rental',
    ipFromBayutApi: 'From Bayut API (real market data)',
    ipMonthlyRentalYear: 'Monthly Rental',
    ipYearAbbrev: 'Y',
    ipThreePercentGrowth: '+3% annual growth',
    ipTotalRentCollected: 'Total Rent Collected',
    ipAllRentOver: 'All rent over',
    ipYears: 'years',
    ipNow: 'Now',
    ipOneYear: '1 year',
    ipProjection5: '5 years projection',
    ipProjection10: '10 years projection',
    ipProjection15: '15 years projection',
    ipProjection20: '20 years projection',
    ipHowProjectionsWork: 'How These Projections Work:',
    ipProjPropertyValueLabel: 'Property Value:',
    ipProjPropertyValueDesc: "Grows at 15% annually based on Dubai's historical appreciation rates",
    ipProjRentalIncomeLabel: 'Rental Income:',
    ipProjRentalIncomeDesc: 'Starting from Bayut API (real market data), grows at 3% annually',
    ipProjTotalReturnLabel: 'Total Return:',
    ipProjTotalReturnDesc: 'Capital appreciation + All rental income collected over time',
    ipCalculateMyPotential: 'Calculate my potential',
    ipViewAll: 'View all',
    ipTrending: 'Trending',
    ipCapitalGrowth: 'Capital growth',
    ipFundedSuffix: 'funded',
    ipFiveYearTotalReturn: '5 year total return',
    ipYearlyInvestmentReturn: 'Yearly investment return',
    ipProjectedNetYield: 'Projected net yield',
    ipUploadOtpToAddProperty: 'Upload an OTP or OQOOD document to automatically add your first property and start tracking your investment performance',

    // ── Analysis / DLD market intelligence ────────────────────────────────
    ipNoPortfolioData: 'No Portfolio Data',
    ipDldMarketIntelligence: 'DLD Market Intelligence',
    ipPoweredByDld: 'Powered by Dubai Land Department Official Data',
    ipUpdatedAutomatically: 'Updated automatically',
    ipCapitalInvested: 'Capital Invested',
    ipDldMarketValue: 'DLD Market Value',
    ipBasedOnDldComparables: 'Based on DLD comparable transactions',
    ipUnrealizedGain: 'Unrealized Gain',
    ipAppreciationLower: 'appreciation',
    ipDldRentalEstimate: 'DLD Rental Estimate',
    ipAnnualRental: 'Annual Rental',
    ipPerYear: '/yr',
    ipPerMonth: '/mo',
    ipGrossYield: 'gross yield',
    ipPropertyLevelAnalysis: 'Property-Level Market Analysis',
    ipEachPropertyCompared: 'Each property compared against real DLD transaction data',
    ipDldDataActive: 'DLD Data Active',
    ipDldValue: 'DLD Value',
    ipDldRent: 'DLD Rent',
    ipBookValue: 'Book Value',
    ipVsPurchase: 'vs purchase',
    ipComparables: 'comparables',
    ipDldRentalMarketData: 'DLD Rental Market Data',
    ipAvgMonthlyRent: 'Avg Monthly Rent',
    ipAvgAnnualRent: 'Avg Annual Rent',
    ipGrossRentalYield: 'Gross Rental Yield',
    ipRentalComparables: 'Rental Comparables',
    ipContracts: 'contracts',
    ipDldPricePerSqm: 'DLD Price/m²',
    ipTotalRoiCapitalRental: 'Total ROI (Capital + Rental)',
    ipCapitalShort: 'Capital',
    ipRentalShort: 'Rental',
    ipRecentDldTransactions: 'Recent DLD Transactions (Comparable)',
    ipRecentDldRentals: 'Recent DLD Rental Contracts (Comparable)',
    ipSize: 'Size',
    ipType: 'Type',
    ipStartDate: 'Start Date',
    ipAnnualRentCol: 'Annual Rent',
    ipMonthlyCol: 'Monthly',
    ipRenewalParen: '(Renewal)',
    ipNewParen: '(New)',
    ipDldDataAppearsAuto: 'DLD market data for this property appears automatically once transactions are registered — updated daily from the Dubai Land Department.',
    ipInvestmentSummary: 'Investment Summary',
    ipTotalGain: 'Total Gain',
    ipRentalYield: 'Rental Yield',
    ipDldDataDisclaimer: 'Data sourced from Dubai Land Department official open records. Values are estimates based on comparable transactions.',
    ipHowWeGetData: 'How We Get This Data',
    ipAllMarketDataSourced: 'All market data is sourced from official Dubai Land Department records',
    ipDsMarketValue: 'Market Value',
    ipDsMarketValueDesc: 'Real sale prices of comparable units in the same project, weighted by recency',
    ipDsPricePerSqft: 'Price/sqft',
    ipDsPricePerSqftDesc: 'Calculated from actual DLD sale transaction amounts divided by property size',
    ipDsRentalPerMonth: 'Rental/mo',
    ipDsRentalPerMonthDesc: 'Average of real Ejari rental contracts registered with DLD for similar units',
    ipDsRentalYield: 'Rental Yield',
    ipDsRentalYieldDesc: 'Annual DLD rental income divided by DLD market value',
    ipDsComparableSales: 'Comparable Sales',
    ipDsComparableSalesDesc: 'Actual DLD-registered sale transactions for units in the same project and similar size',
    ipDsComparableRentals: 'Comparable Rentals',
    ipDsComparableRentalsDesc: 'Actual Ejari tenancy contracts registered with DLD for similar properties',
    ipSourceLabel: 'Source:',
    ipDldApiNote: '— Dubai Land Department Official Open Data API. Data is updated automatically every 6 hours. Values are estimates based on comparable transactions and may differ from actual market value.',
    ipUploadOtpToUnlock: 'Upload OTP documents to unlock DLD Market Intelligence — real valuations, rental data, and comparable analysis powered by Dubai Land Department.',
    ipUploadOtpDocuments: 'Upload OTP Documents',

    // ── News ──────────────────────────────────────────────────────────────
    ipFailedFetchNews: 'Failed to fetch news. Please try again later.',
    ipNoRecentNews: 'No recent news found.',

    // ── Experience / testimonials ─────────────────────────────────────────
    ipInvestorsWhoTrust: 'Investors who trust us',
    ipTestimonial1Title: 'Stock market professional & real estate investor',
    ipTestimonial1Quote: 'With Michael, I have an absolute expert at my side. And he not only tells, but also invests himself.',
    ipTestimonial2Title: "Europe's No. 1 personality trainer",
    ipTestimonial2Quote: 'The many years of experience, the professional handling as well as the excellent expertise make Asset Desk the No. 1 for German-speaking investors for me.',
    ipTestimonial3Title: 'Investor | Real Estate Entrepreneur',
    ipTestimonial3Quote: 'The combination of market dynamics, momentum and the possible purchase and sales prices in the short period of time is currently only offered by Dubai.',

    // ── Events (congress) ─────────────────────────────────────────────────
    ipKongressSpeakers: 'Speakers',

    // ── Services marketplace / appointments ───────────────────────────────
    ipCompleteAllSteps: 'Please complete all steps to book an appointment.',
    ipAppointmentBookedPre: 'Appointment successfully booked for "',
    ipAppointmentBookedOn: '" on',
    ipAppointmentBookedAt: 'at',
    ipMeetingLinkRequested: 'Meeting link request sent. You will receive the link via email shortly.',
    ipFailedSendRequest: 'Failed to send request. Please try again or contact support.',
    ipBooking: 'Booking...',
    ipConfirmAppointment: 'Confirm Appointment',
    ipSelectDateStep: '3. Select a Date',
    ipAtTime: 'at',
    ipDubaiTimeParen: '(Dubai Time)',
    ipCorporateServicesDesc: 'Strategic advisory for international investors.',
    ipConveyanceServicesDesc: 'Professional property transaction services.',

    // ── Revenue trend (Home) ──────────────────────────────────────────────
    ipRevenueTrend: 'Revenue trend',
    ipRevenueCollected: 'Rent collected',
    ipRevenueLast12: 'Last 12 months',
    ipRevenueUpcoming: 'Next 12 months',
    ipRevenueEmpty: 'Rental income appears here as soon as a lease is signed and the first cheque falls due.',
    ipRevenueFromLeases: 'From signed tenancy contracts',
    ipRevenueDue: 'due',

    // ── Portfolio value trend (Home) ──────────────────────────────────────
    ipViewPortfolio: 'View Portfolio',
    ipValueToday: 'Today',
    ipValueFirstBuy: 'First purchase',
    ipValueCostNote: 'Steps at each purchase; closes at today’s DLD valuation.',

    // ── Next payout / profit this year (Home) ─────────────────────────────
    ipNextPayout: 'Next payout',
    ipChequeHeld: 'Cheque held',
    ipPayoutProgress: '{n} of {total} rent cheques banked across your tenancies',
    ipPayoutDate: 'Payout date',
    ipPayoutProperty: 'Property',
    ipProfitAllLeases: 'Rental profit · all leases',
    ipLeaseBasis: 'Rent from {n} signed leases, minus running costs',
    ipNetYieldPa: 'net yield p.a.',
    ipCollected: 'Collected',
    ipOfLeaseRent: 'of lease rent',
    ipUnitsLet: 'Units let',
    ipMonthsN: '{n} months',
    ipRentShort: 'Rent',
    ipCostsShort: 'Costs',
    ipYieldShort: 'Net yield',
    ipChequesShort: 'Cheques',
    ipLeasePassed: '{p}% of lease passed',
    ipDaysLeft: '{n} days left',
    ipLeaseEnded: 'Lease ended',
    ipScUnpublished: 'No service charge published for this building, so the net excludes it.',
    ipOnPurchasePrice: 'on purchase price',
    ipRentLive: 'Rent earned in {year} · live',
    ipRentPerDay: '+ AED {day} a day from {n} signed leases',
    ipTotalInvested: 'Total invested',
    ipInvestedSub: '{n} properties · purchase prices',
    ipEstValueLive: 'Est. market value · live',
    ipCalcTitle: 'How this is calculated',
    ipCalcValue: 'DLD valuation today, {n} properties',
    ipCalcInvested: 'Total invested (purchase prices)',
    ipCalcGain: 'Difference',
    ipCalcBasis: 'Growth measured on {n} properties: paid → DLD value today',
    ipCalcHeld: 'Average time held (weighted by price)',
    ipCalcYears: 'years',
    ipCalcRate: 'Annual growth rate',
    ipCalcDay: 'Growth per day',
    ipCalcExcluded: 'Left out of the growth rate (but included in the value): {low} low-confidence valuations and {nodate} properties without a purchase date. The counter grows the DLD valuation at this rate from the last DLD update. It is an estimate, not a price anyone has offered.',
    ipValueLoading: 'Loading the Dubai Land Department valuation…',
    ipValueNoRate: 'Not enough valuation history yet to estimate a growth rate.',
    ipVsInvested: '{gain} ({pct}%) vs invested',
    ipGrowingEst: 'growing est. {rate}% p.a., about AED {day} a day',
    ipTrendEst: 'trend on {n} reliably valued units: est. {rate}% p.a., about AED {day} a day',
    ipValueSource: 'Estimate from Dubai Land Department transaction valuations. Growth rate measured from purchase price to today on {n} units valued with high or medium confidence.',
    ipValueSourceDld: 'Each unit valued on registered Dubai Land Department sales in its own building. Updated when new sales are registered.',
    ipIncome: 'Rental income',
    ipExpenses: 'Running costs',
  },

  de: {
    // ── Shell / navigation ────────────────────────────────────────────────
    ipPartner: 'Partner',
    ipUser: 'Nutzer',
    ipPropertyMgmtShort: 'Verwaltung',
    ipFeed: 'Neuigkeiten',
    // Vier-Tab-Leiste. Siehe englische Fassung fuer die Begruendung.
    ipNavHome: 'Start',
    ipNavConsole: 'Konsole',
    ipNavProperties: 'Immobilien',
    ipNavPortfolio: 'Portfolio',
    ipTabReport: 'Bericht',
    ipTabManagement: 'Immobilienverwaltung',
    ipTabValuation: 'Bewertung',
    // Corporate: trade licences, visas and transactions from the CRM.
    ipTabCorporate: 'Unternehmen',
    // InvestMenter Brain: DLD vs the agency deal history vs the AI.
    ipTabBrain: 'Asset Desk KI',
    imbWorking: 'Die Asset Desk KI arbeitet für Sie',
    imbTaskPlans: 'Ihre Zahlungspläne werden geladen',
    imbTaskValuation: 'Ihr Portfolio wird mit DLD-Daten bewertet',
    imbTaskPortfolio: 'Ihr Portfolio wird geladen',
    imbTaskDealHistory: 'Asset Desk Deal-Historie wird gelesen',
    imbPage1Eyebrow: '01 · Öffentliche Daten',
    imbPage1Title: 'DLD-Daten kann jeder vergleichen.',
    imbPage1Lead: 'Das Dubai Land Department veröffentlicht jeden Verkauf in Dubai. Jede Website und jede KI kann das lesen. Hier ist Ihr Portfolio im Vergleich.',
    imbPaid: 'Bezahlt',
    imbDld: 'DLD-Wert',
    imbDiff: 'Differenz',
    imbNoUnits: 'Noch keine Immobilien mit Kaufpreis.',
    imbDldLoading: 'DLD-Bewertung wird geladen…',
    imbDldNone: 'Die DLD-Bewertung ist gerade nicht verfügbar.',
    imbNoDld: 'Zu wenige Verkäufe',
    imbComps: '{n} Vergleichsverkäufe',
    imbConfLow: 'geringe Sicherheit',
    imbMoreUnits: '+ {n} weitere Immobilien unter Bewertung',
    imbDldSource: 'Quelle: Transaktionen des Dubai Land Department · Stand {date}',
    imbDldSourceNoDate: 'Quelle: Transaktionen des Dubai Land Department',
    imbNext: 'Weiter',
    imbBack: 'Zurück',
    imbPage2Eyebrow: '02 · Nur Asset Desk',
    imbPage2Title: 'Nur Asset Desk hat die eigene Deal-Historie.',
    imbPage2Lead: 'Nicht jeder kann die Daten des größten deutschsprachigen Immobilienmaklers einbringen: was unsere Kunden tatsächlich bezahlt haben, wann, in welchen Projekten und wie ihre Weiterverkäufe ausgingen. Das DLD veröffentlicht das nicht. Niemand sonst hat es.',
    imbHistLoading: 'Deal-Historie wird geladen…',
    imbHistNone: 'Noch kein Projekt hat genug Kunden-Deals für eine Anzeige.',
    imbHistError: 'Die Deal-Historie konnte gerade nicht geladen werden.',
    imbHistAsOf: 'Stand {date}; die neueste Aktualisierung konnte nicht geladen werden.',
    imbRetry: 'Erneut versuchen',
    imbDeals: 'Kunden-Deals',
    imbClients: 'Kunden',
    imbProjects: 'Projekte',
    imbBought: 'gekauft',
    imbSince: 'seit {year}',
    imbResaleAll: 'Weiterverkäufe: Median {roi}% p. a.',
    imbYouVsClients: 'Sie im Vergleich zu anderen Asset-Desk-Kunden',
    imbDealsBy: '{deals} Deals · {clients} Kunden',
    imbYouPaid: 'Sie zahlten',
    imbClientsMedian: 'Median der Kunden',
    imbPerSqft: '/sqft',
    imbTopProjects: 'Wo unsere Kunden kaufen',
    imbResale: 'Weiterverkäufe {roi}% p. a.',
    imbPrivacy: 'Anonymisiert: keine Namen, keine Einheitennummern. Ein Projekt erscheint erst, wenn {n} oder mehr verschiedene Kunden dort einen Deal haben; der Preis pro sqft ist auf AED 50 gerundet.',
    imbPage3Eyebrow: '03 · Die Asset Desk KI',
    imbPage3Kicker: 'Eine normale KI kann das nicht.',
    imbPage3Title: 'Die Asset Desk KI kann es.',
    imbPage3Lead: 'Fragen Sie ChatGPT, bekommen Sie dasselbe wie alle anderen: öffentliche Daten und nur das, was Sie selbst hineinschreiben. Die Asset Desk KI liest die DLD-Daten, unsere eigene Deal-Historie und Ihr Portfolio zusammen und antwortet mit allen dreien.',
    imbAskTitle: 'Fragen Sie sie',
    imbPage4Eyebrow: '04 · Die eigentliche Frage',
    imbPage4Kicker: 'Sind Sie Immobilienexperte?',
    imbPage4Title: 'Oder sind Sie richtig gut in dem, was Sie selbst tun?',
    imbPage4Lead: 'Ein guter Arzt, Anwalt oder Softwareentwickler sollte das zu 100 % tun und sich das beste Wissen von Leuten holen, die jeden Tag und jede Nacht mit Immobilien arbeiten.',
    imbMissTitle: 'Ohne die Asset Desk KI verpassen Sie',
    imbMiss1: 'Was andere Investoren in Ihren Projekten wirklich bezahlt haben, nicht nur, was das DLD veröffentlicht.',
    imbMiss2: 'Wie Weiterverkäufe in diesen Projekten tatsächlich ausgegangen sind.',
    imbMiss3: 'Eine KI, die mit diesem Wissen antwortet, statt mit dem, was alle anderen bekommen.',
    imbClose: 'Seien Sie klug. Nutzen Sie das Wissen der Asset Desk KI, Ihres AI Wealth Co-Pilot.',
    imbCta: 'Asset Desk KI fragen',
    imbCtaMessage: 'Ich möchte die passende Investition in Dubai für mich finden.',
    imbAsk1: 'Habe ich in {project} einen guten Preis bezahlt, im Vergleich zum DLD und zu anderen Asset-Desk-Kunden?',
    imbAsk1Generic: 'Habe ich einen guten Preis bezahlt, im Vergleich zum DLD und zu anderen Asset-Desk-Kunden?',
    imbAsk2: 'Welche meiner Immobilien hat laut DLD-Wert am meisten zugelegt?',
    imbAsk3: 'Wie haben Asset-Desk-Kunden bei Weiterverkäufen abgeschnitten?',
    ipNavMore: 'Mehr',
    ipMoneyPlans: 'Zahlungen',
    ipHubReport: 'Portfolio-Bericht',
    ipHubValuations: 'Bewertungen',
    ipHubBackToList: 'Zurueck zu Immobilien',
    ipMoreTools: 'Rechner & Tools',
    ipPersonalDocsSub: 'Reisepass, Visum und Emirates ID',
    ipDownload: 'Herunterladen',
    ipDocument: 'Dokument',
    ipProcessingDocumentAI: 'Dokument wird mit KI verarbeitet...',
    ipChangeRole: 'Rolle wechseln (Eigentümer / Mieter / Gast)',
    ipAboutUs: 'Über uns',

    // ── Auth: shared form chrome ──────────────────────────────────────────
    ipSignIn: 'Anmelden',
    ipCreateAccount: 'Konto erstellen',
    ipUsername: 'Benutzername',
    ipPassword: 'Passwort',
    ipForgotEmail: 'E-Mail vergessen?',
    ipForgotPassword: 'Passwort vergessen?',
    ipEnterYourEmail: 'E-Mail-Adresse eingeben',
    ipEnterYourPhone: 'Telefonnummer eingeben',
    ipEnterYourPassword: 'Passwort eingeben',
    ipEnterFullName: 'Vollständigen Namen eingeben',
    ipSelectNationality: 'Nationalität auswählen',
    ipOptionalParen: '(optional)',
    ipCannotBeChanged: '(nicht änderbar)',
    ipHidePassword: 'Passwort verbergen',
    ipShowPassword: 'Passwort anzeigen',
    ipPrivateClientAccess: 'PRIVATE CLIENT ACCESS',
    ipJoinInvestMenter: 'Bei Asset Desk registrieren',
    ipAccessPremiumPortfolio: 'Zugang zu Ihrem Premium-Investmentportfolio',
    ipCreatePremiumAccount: 'Erstellen Sie Ihr Premium-Investorenkonto',
    ipNoAccountSignUp: 'Noch kein Konto? Jetzt registrieren',
    ipHaveAccountSignIn: 'Sie haben bereits ein Konto? Anmelden',
    ipEnterEmailFirst: 'Bitte geben Sie zuerst Ihre E-Mail-Adresse ein',
    ipSignInWithFaceRecognition: 'Mit Gesichtserkennung anmelden',
    ipAgeAndAgree: 'Ich bin 18 Jahre oder älter und akzeptiere die',
    ipTermsOfService: 'Nutzungsbedingungen',
    ipAnd: 'und die',
    ipPrivacyPolicy: 'Datenschutzerklärung',
    ipConfirmAction: 'Aktion bestätigen',
    ipYes: 'Ja',
    ipNo: 'Nein',
    ipBackToLogin: 'Zurück zur Anmeldung',

    // ── Auth: account lockout ─────────────────────────────────────────────
    ipAccountLocked: 'Konto gesperrt',
    ipAccountLockedDesc: 'Ihr Konto wurde aufgrund zu vieler fehlgeschlagener Anmeldeversuche gesperrt.',
    ipResetPasswordToUnlock: 'Passwort zurücksetzen und entsperren',
    ipWarningPrefix: 'Warnung: Noch',
    ipOneAttempt: '1 Versuch',
    ipAttemptsPlural: 'Versuche',
    ipBeforeLockoutSuffix: 'übrig, bevor Ihr Konto gesperrt wird.',

    // ── Auth: messages ────────────────────────────────────────────────────
    ipEmailNotVerified: 'Ihre E-Mail-Adresse ist noch nicht bestätigt. Bitte prüfen Sie Ihren Posteingang und klicken Sie auf den Bestätigungslink.',
    ipInvalidEmailOrPassword: 'E-Mail-Adresse oder Passwort ist falsch',
    ipLoginFailed: 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    ipEnterPhoneNumber: 'Bitte geben Sie Ihre Telefonnummer ein',
    ipFailedToSendCode: 'Code konnte nicht gesendet werden',
    ipConnectionError: 'Verbindungsfehler. Bitte versuchen Sie es erneut.',
    ipEnterVerificationCode: 'Bitte geben Sie den Bestätigungscode ein',
    ipEnterFull6DigitCode: 'Bitte geben Sie den vollständigen 6-stelligen Code ein',
    ipInvalidCode: 'Ungültiger Code',
    ipVerificationFailed: 'Bestätigung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    ipEnter6DigitCode: 'Bitte geben Sie einen 6-stelligen Code ein',
    ipInvalidVerificationCodeRetry: 'Ungültiger Bestätigungscode. Bitte versuchen Sie es erneut.',
    ipInvalidVerificationCode: 'Ungültiger Bestätigungscode.',
    ipPasswordsDoNotMatch: 'Die Passwörter stimmen nicht überein',
    ipFillAllRequiredFields: 'Bitte füllen Sie alle Pflichtfelder aus',
    ipAcceptTermsToContinue: 'Bitte akzeptieren Sie die Nutzungsbedingungen und die Datenschutzerklärung, um fortzufahren.',
    ipSignupFailed: 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.',
    ipSignupFailedConnection: 'Registrierung fehlgeschlagen. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    ipAccountCreatedPendingDocs: 'Konto erstellt! Bitte prüfen Sie Ihre E-Mails und klicken Sie auf den Bestätigungslink. Ihre Dokumente werden nach der Bestätigung automatisch mit Ihrem Konto verknüpft.',
    ipAccountCreatedVerify: 'Konto erstellt! Bitte prüfen Sie Ihre E-Mails, klicken Sie auf den Bestätigungslink und melden Sie sich anschließend hier an.',
    ipUaePassFailed: 'Authentifizierung mit UAE Pass fehlgeschlagen. Bitte versuchen Sie es erneut.',
    ipEnterEmailAddress: 'Bitte geben Sie Ihre E-Mail-Adresse ein',
    ipFailedSendResetCode: 'Der Code zum Zurücksetzen konnte nicht gesendet werden. Bitte versuchen Sie es erneut.',
    ipNetworkErrorRetry: 'Netzwerkfehler. Bitte prüfen Sie Ihre Verbindung und versuchen Sie es erneut.',
    ipNetworkError: 'Netzwerkfehler. Bitte versuchen Sie es erneut.',
    ipFillBothPasswordFields: 'Bitte füllen Sie beide Passwortfelder aus',
    ipFillPhoneAndNationality: 'Bitte geben Sie Telefonnummer und Nationalität ein',
    ipPasswordMin6: 'Das Passwort muss mindestens 6 Zeichen lang sein',
    ipPasswordResetSuccess: 'Ihr Passwort wurde erfolgreich zurückgesetzt! Sie können sich jetzt mit Ihrem neuen Passwort anmelden.',
    ipFailedResetPassword: 'Das Passwort konnte nicht zurückgesetzt werden. Bitte versuchen Sie es erneut.',
    ipRecoveredEmailIs: 'Ihre E-Mail-Adresse lautet:',
    ipRecoveredEmailNote: 'Aus Sicherheitsgründen haben wir Ihre E-Mail-Adresse teilweise verborgen. Einen vollständigen Wiederherstellungslink haben wir an Ihre registrierte Telefonnummer gesendet.',
    ipNoAccountForPhoneNationality: 'Es wurde kein Konto mit dieser Kombination aus Telefonnummer und Nationalität gefunden.',

    // ── Auth: two-factor ──────────────────────────────────────────────────
    ipTwoFactorAuthTitle: 'Zwei-Faktor-Authentifizierung',
    ipSetUpTwoFactorAuth: 'Zwei-Faktor-Authentifizierung einrichten',
    ipEnhanceSecurityWith2fa: 'Erhöhen Sie die Sicherheit Ihres Kontos mit 2FA',
    ipEnter6DigitCodeLabel: 'Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein',
    ipEnter6DigitFromAuthenticator: 'Bitte geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein',
    ipAuthenticatorApp: 'Authenticator-App',
    ipBackupCode: 'Backup-Code',
    ipAuthenticationCode: 'Authentifizierungscode',
    ipEnterBackupCode: 'Backup-Code eingeben',
    ipOpenAuthenticatorApp: 'Öffnen Sie Ihre Authenticator-App, um Ihren Authentifizierungscode anzuzeigen',
    ipTrustDevice30Days: 'Diesem Gerät 30 Tage vertrauen',
    ipVerifying: 'Wird geprüft...',
    ipVerifyAndSignIn: 'Prüfen & anmelden',
    ipLostAuthenticatorAccess: 'Kein Zugriff mehr auf Ihre Authenticator-App?',
    ipContactSupportRecovery: 'Bitte wenden Sie sich an support@investmenter.com, um Ihr Konto wiederherzustellen.',
    ipContactSupport: 'Support kontaktieren',
    ipGenerating: 'Wird erstellt...',
    ipGenerateQrCode: 'QR-Code erstellen',
    ipStep1ScanQr: 'Schritt 1: QR-Code scannen',
    ipBackupCodesCopied: 'Backup-Codes in die Zwischenablage kopiert!',
    ipEnabling: 'Wird aktiviert...',
    ipEnable2fa: '2FA aktivieren',
    ipDisable2fa: '2FA deaktivieren',
    ipRegenerateBackupCodes: 'Backup-Codes neu erstellen',
    ipYourBackupCodes: 'Ihre Backup-Codes',
    ipSkip2faConfirm: 'Möchten Sie die 2FA-Einrichtung wirklich überspringen? Sie können sie später in Ihren Sicherheitseinstellungen aktivieren.',
    ipSkipForNow: 'Vorerst überspringen',
    ipFailedSetup2fa: '2FA konnte nicht eingerichtet werden. Bitte versuchen Sie es erneut.',
    ipFailedEnable2fa: '2FA konnte nicht aktiviert werden. Bitte versuchen Sie es erneut.',
    ipFailedDisable2fa: '2FA konnte nicht deaktiviert werden. Bitte versuchen Sie es erneut.',
    ipFailedDisable2faCheckCode: '2FA konnte nicht deaktiviert werden. Bitte prüfen Sie Ihren Code und versuchen Sie es erneut.',
    ipTwoFaEnabledAlert: 'Die Zwei-Faktor-Authentifizierung wurde erfolgreich aktiviert!',
    ipTwoFaDisabledAlert: 'Die Zwei-Faktor-Authentifizierung wurde deaktiviert.',
    ipInvalidCodeCheckAuthenticator: 'Ungültiger Code. Bitte prüfen Sie Ihre Authenticator-App.',
    ipEnterCodeToDisable2fa: 'Geben Sie Ihren aktuellen Authenticator-Code ein, um 2FA zu deaktivieren:',
    ipBackupCodesShownOnce: 'Backup-Codes werden nur einmal bei der ersten Einrichtung von 2FA angezeigt. Erstellen Sie neue Codes, um weitere zu erhalten.',
    ipRegenerateBackupCodesConfirm: 'Backup-Codes neu erstellen? Ihre alten Codes sind danach ungültig.',
    ipNewBackupCodesGenerated: 'Neue Backup-Codes wurden erstellt. Speichern Sie sie jetzt — sie werden nicht erneut angezeigt.',
    ipFailedRegenerateBackupCodes: 'Die Backup-Codes konnten nicht neu erstellt werden.',

    // ── Auth: WhatsApp / SMS sign-in ──────────────────────────────────────
    ipSignInWithWhatsApp: 'Mit WhatsApp anmelden',
    ipEnterVerificationCodeTitle: 'Bestätigungscode eingeben',
    ipEnterRegisteredPhoneWhatsApp: 'Geben Sie Ihre registrierte Telefonnummer ein, um einen Code per WhatsApp zu erhalten',
    ipWeSentWhatsAppCodeTo: 'Wir haben einen WhatsApp-Code gesendet an',
    ipSending: 'Wird gesendet...',
    ipSendCode: 'Code senden',
    ipResendCode: 'Code erneut senden',
    ipEnter6DigitCodePlaceholder: '6-stelligen Code eingeben',

    // ── Auth: password reset / email recovery ──────────────────────────────
    ipResetPassword: 'Passwort zurücksetzen',
    ipEnterYourNewPassword: 'Geben Sie Ihr neues Passwort ein',
    ipEnterCodeSentToEmail: 'Geben Sie den Bestätigungscode ein, den wir Ihnen per E-Mail gesendet haben',
    ipEnterEmailForResetCode: 'Geben Sie Ihre E-Mail-Adresse ein, um einen Code zum Zurücksetzen des Passworts zu erhalten',
    ipEnterRegisteredEmail: 'Registrierte E-Mail-Adresse eingeben',
    ipVerificationCodeLabel: 'Bestätigungscode',
    ipCheckEmailForCode: 'Den Bestätigungscode finden Sie in Ihrem E-Mail-Postfach',
    ipNewCodeSent: 'Ein neuer Code wurde an Ihre E-Mail-Adresse gesendet.',
    ipFailedResendCode: 'Der Code konnte nicht erneut gesendet werden.',
    ipDidntReceiveResend: 'Nichts erhalten? Code erneut senden',
    ipConfirmNewPassword: 'Neues Passwort bestätigen',
    ipConfirmNewPasswordPlaceholder: 'Neues Passwort bestätigen',
    ipProcessing: 'Wird verarbeitet...',
    ipVerifyCode: 'Code prüfen',
    ipSendResetCode: 'Code zum Zurücksetzen senden',
    ipRecoverEmailAddress: 'E-Mail-Adresse wiederherstellen',
    ipRecoverEmailDesc: 'Geben Sie Ihre Telefonnummer und Nationalität ein, um Ihre E-Mail-Adresse wiederherzustellen',
    ipEnterRegisteredPhone: 'Registrierte Telefonnummer eingeben',
    ipYourEmailAddress: 'Ihre E-Mail-Adresse',
    ipSearching: 'Wird gesucht...',
    ipFindMyEmail: 'Meine E-Mail-Adresse finden',

    // ── Auth: face / biometric modal ──────────────────────────────────────
    ipBiometricVerification: 'BIOMETRISCHE VERIFIZIERUNG',
    ipFaceAuthentication: 'Gesichtserkennung',
    ipPositionFaceCentre: 'Positionieren Sie Ihr Gesicht in der Mitte der Kamera',
    ipAuthenticating: 'Authentifizierung läuft...',
    ipAuthenticate: 'Authentifizieren',
    ipFaceDataEncryptedSecure: 'Ihre Gesichtsdaten werden verschlüsselt und sicher gespeichert',
    ipEnrollYourFace: 'Gesicht registrieren',
    ipCapturing: 'Aufnahme läuft...',
    ipCaptureFace: 'Gesicht aufnehmen',

    // ── Documents: upload feedback ────────────────────────────────────────
    ipDuplicateDetected: '[X] Doppeltes Dokument erkannt',
    ipDuplicateCannotUploadA: 'Upload nicht möglich: Für diese Einheit existiert bereits ein',
    ipDuplicateCannotUploadB: 'Dokument.',
    ipDocumentLabel: 'Dokument:',
    ipUploadedOnLabel: 'Hochgeladen am:',
    ipDuplicateTipA: 'Tipp: Bitte löschen Sie zuerst das vorhandene',
    ipDuplicateTipB: 'Dokument, wenn Sie ein neues hochladen möchten.',
    ipUploadFailedTitle: '[X] Upload fehlgeschlagen',
    ipDeleteExistingFirst: 'Bitte löschen Sie zuerst das vorhandene Dokument.',
    ipErrorLabel: 'Fehler:',
    ipUnknownErrorOccurred: 'Unbekannter Fehler aufgetreten',
    ipUnknownError: 'Unbekannter Fehler',
    ipTryAgainOrContactSupport: 'Bitte versuchen Sie es erneut oder wenden Sie sich an den Support, falls das Problem weiterhin besteht.',
    ipTryAgainOrContactSupportShort: 'Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.',
    ipUploadSuccessTitle: '[OK] Upload erfolgreich!',
    ipTypeLabel: 'Typ:',
    ipCategoryLabel: 'Kategorie:',
    ipAiExtractedData: 'Die KI hat die Immobiliendaten ausgelesen.',
    ipTeamWillVerify: 'Unser Team prüft die Daten, bevor Ihre Immobiliendaten aktualisiert werden.',
    ipStatusChangeOnceVerified: 'Sie sehen die Statusänderung, sobald die Prüfung abgeschlossen ist.',
    ipDocUploadedProcessed: 'Das Dokument wurde erfolgreich hochgeladen und verarbeitet.',
    ipProcessingErrorTitle: '[X] Verarbeitungsfehler',
    ipProcessingErrorBody: 'Bei der Verarbeitung des Dokuments ist ein Fehler aufgetreten:',
    ipLoginToDownload: 'Bitte melden Sie sich an, um Dokumente herunterzuladen.',
    ipCouldNotDownload: 'Das Dokument konnte nicht heruntergeladen werden. Bitte versuchen Sie es erneut.',
    ipLoginToView: 'Bitte melden Sie sich an, um Dokumente anzuzeigen.',
    ipCouldNotOpen: 'Das Dokument konnte nicht geöffnet werden. Bitte versuchen Sie es erneut.',
    ipSelectUnitFirstAlert: 'Bitte wählen Sie zuerst eine Einheit aus oder fügen Sie eine neue hinzu',
    ipFileTooLarge: 'Datei zu groß. Bitte wählen Sie eine Datei kleiner als 100 MB.',
    ipMax20Files: 'Maximal 20 Dateien gleichzeitig. Bitte wählen Sie weniger Dateien aus.',
    ipFileTooLargeNamedPre: 'Die Datei "',
    ipFileTooLargeNamedPost: '" ist zu groß (max. 100 MB). Bitte entfernen Sie sie und versuchen Sie es erneut.',
    ipPreparingFiles: 'Dateien werden vorbereitet...',
    ipReading: 'Wird gelesen',
    ipAiAnalyzing: 'Die KI analysiert Ihre Dokumente...',
    ipDocsUploadedSuccessfully: 'Dokument(e) erfolgreich hochgeladen!',
    ipDocumentsDetected: 'Erkannte Dokumente:',
    ipUnmatchedAdminWillAssign: '(nicht zugeordnet – wird vom Team zugewiesen)',
    ipPendingAdminVerification: 'Alle Dokumente warten auf die Prüfung durch unser Team.\nSie erscheinen in Ihrer App, sobald sie geprüft wurden.',
    ipFilesFailed: 'Datei(en) fehlgeschlagen:',
    ipUploadFailedLabel: 'Upload fehlgeschlagen:',
    ipUploadErrorLabel: 'Upload-Fehler:',
    ipPleaseTryAgain: 'Bitte versuchen Sie es erneut',
    ipFillRequiredUnitFields: 'Bitte füllen Sie die Pflichtfelder aus: Name, Einheitennummer und Projekt',
    ipSessionExpired: 'Sitzung abgelaufen. Bitte melden Sie sich erneut an.',
    ipUnitAddedPre: '[OK] Einheit "',
    ipUnitAddedPost: '" wurde erfolgreich hinzugefügt! Sie können jetzt Dokumente hochladen.',
    ipUnitCreationFailed: 'Die Einheit konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
    ipFailedToCreateUnit: 'Die Einheit konnte nicht erstellt werden:',

    // ── AI assistant ──────────────────────────────────────────────────────
    ipAiDefaultGreeting: 'Ich helfe Ihnen gerne! Wie kann ich Sie bei Ihrer Immobilieninvestition in Dubai unterstützen?',
    ipAiErrorMessage: 'Entschuldigung, Ihre Anfrage konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut oder nutzen Sie den WhatsApp-Support für sofortige Hilfe.',
    ipTryAsking: 'Fragen Sie zum Beispiel:',
    ipChatSuggestion1: 'Wie entwickelt sich der Immobilienmarkt in Dubai derzeit?',
    ipChatSuggestion2: 'Wie kann ich meine Mietrendite maximieren?',
    ipChatSuggestion3: 'Welche Dokumente benötige ich für eine Immobilieninvestition?',
    ipChatSuggestion4: 'Erzählen Sie mir vom Golden Visa durch Immobilienbesitz',
    ipChatOnWhatsApp: 'Per WhatsApp chatten',
    ipOnline: 'Online',
    ipSupportTeam: 'Support-Team',
    ipBusinessHours: 'Erreichbar Mo–Fr, 9–18 Uhr (Dubai-Zeit)',

    // ── Profile ───────────────────────────────────────────────────────────
    ipProfileUpdated: 'Profil erfolgreich aktualisiert!',
    ipFailedUpdateProfile: 'Das Profil konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.',
    ipFillAllPasswordFields: 'Bitte füllen Sie alle Passwortfelder aus',
    ipCurrentPasswordIncorrect: 'Das aktuelle Passwort ist falsch',
    ipNewPasswordMin6: 'Das neue Passwort muss mindestens 6 Zeichen lang sein',
    ipNewPasswordsDoNotMatch: 'Die neuen Passwörter stimmen nicht überein',
    ipFailed: 'Fehlgeschlagen',
    ipPasswordChanged: 'Passwort erfolgreich geändert!',
    ipFailedChangePassword: 'Das Passwort konnte nicht geändert werden. Bitte versuchen Sie es erneut.',
    ipFailedDeleteAccount: 'Das Konto konnte nicht gelöscht werden',
    ipFailedDeleteAccountRetry: 'Das Konto konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.',
    ipSaving: 'Wird gespeichert...',
    ipSaveChanges: 'Änderungen speichern',
    ipUpdating: 'Wird aktualisiert...',
    ipUpdatePassword: 'Passwort aktualisieren',
    ipAccountStatistics: 'Kontostatistik',
    ipAccountActivitySummary: 'Zusammenfassung Ihrer Kontoaktivität',
    ipDeleteAccount: 'Konto löschen',
    ipDeleteAccountDesc: 'Löschen Sie Ihr Konto und alle zugehörigen Daten dauerhaft. Dieser Vorgang kann nicht rückgängig gemacht werden.',
    ipDeleteMyAccount: 'Mein Konto löschen',
    ipDeleteAccountWarning: 'Warnung: Hiermit wird Ihr Konto dauerhaft gelöscht',
    ipDeleteBullet1: 'Alle Ihre personenbezogenen Daten werden entfernt',
    ipDeleteBullet2: 'Ihre Dokumente und Portfoliodaten werden gelöscht',
    ipDeleteBullet3: 'Dieser Vorgang kann nicht rückgängig gemacht werden',
    ipTypeToConfirmPre: 'Geben Sie',
    ipTypeToConfirmPost: 'ein, um zu bestätigen:',
    ipTypeDeleteToConfirm: 'DELETE eingeben, um zu bestätigen',
    ipDeleting: 'Wird gelöscht...',
    ipPermanentlyDeleteAccount: 'Konto dauerhaft löschen',

    // ── Dashboard (projections) ───────────────────────────────────────────
    ipInvestmentGrowth: 'Wertentwicklung der Investition',
    ipPortfolioValueLabel: 'Portfoliowert',
    ipInvest: 'Investieren',
    ipAllTimeReturns: 'Gesamtrendite seit Beginn',
    ipTotalRentalIncome: 'Gesamte Mieteinnahmen',
    ipAnnualIncome: 'Jährliche Einnahmen',
    ipTotalAppreciation: 'Gesamte Wertsteigerung',
    ipFromPurchaseToToday: 'Vom Kaufdatum bis heute',
    ipGrowthRate: 'Wachstumsrate',
    ipPurchaseValueColon: 'Kaufwert:',
    ipCurrentValueColon: 'Aktueller Wert:',
    ipCapitalGainColon: 'Kapitalgewinn:',
    ipCurrentColon: 'Aktuell:',
    ipPropertyValueYear: 'Immobilienwert J',
    ipGrowthSuffix: 'Wachstum',
    ipCumulativeRentalYear: 'Kumulierte Miete J',
    ipTotalRentCollectedShort: 'Gesamte vereinnahmte Miete',
    ipTotalReturnYear: 'Gesamtrendite J',
    ipCapitalPlusRental: 'Kapital + Miete',
    ipPropertyValueGrowthChart: 'Wertentwicklung der Immobilie (15 % Wertsteigerung p. a.)',
    ipCumulativeRentalChart: 'Kumulierte Mieteinnahmen (3 % Wachstum p. a.)',
    ipStartingMonthlyRental: 'Monatsmiete zu Beginn',
    ipFromBayutApi: 'Aus der Bayut-API (echte Marktdaten)',
    ipMonthlyRentalYear: 'Monatsmiete',
    ipYearAbbrev: 'J',
    ipThreePercentGrowth: '+3 % Wachstum p. a.',
    ipTotalRentCollected: 'Gesamte vereinnahmte Miete',
    ipAllRentOver: 'Gesamte Miete über',
    ipYears: 'Jahre',
    ipNow: 'Jetzt',
    ipOneYear: '1 Jahr',
    ipProjection5: 'Prognose über 5 Jahre',
    ipProjection10: 'Prognose über 10 Jahre',
    ipProjection15: 'Prognose über 15 Jahre',
    ipProjection20: 'Prognose über 20 Jahre',
    ipHowProjectionsWork: 'So entstehen diese Prognosen:',
    ipProjPropertyValueLabel: 'Immobilienwert:',
    ipProjPropertyValueDesc: 'Wächst um 15 % pro Jahr auf Basis der historischen Wertsteigerung in Dubai',
    ipProjRentalIncomeLabel: 'Mieteinnahmen:',
    ipProjRentalIncomeDesc: 'Ausgehend von der Bayut-API (echte Marktdaten), Wachstum von 3 % pro Jahr',
    ipProjTotalReturnLabel: 'Gesamtrendite:',
    ipProjTotalReturnDesc: 'Wertsteigerung + alle über die Zeit vereinnahmten Mieteinnahmen',
    ipCalculateMyPotential: 'Mein Potenzial berechnen',
    ipViewAll: 'Alle anzeigen',
    ipTrending: 'Im Trend',
    ipCapitalGrowth: 'Wertsteigerung',
    ipFundedSuffix: 'finanziert',
    ipFiveYearTotalReturn: 'Gesamtrendite über 5 Jahre',
    ipYearlyInvestmentReturn: 'Jährliche Rendite',
    ipProjectedNetYield: 'Erwartete Nettorendite',
    ipUploadOtpToAddProperty: 'Laden Sie ein OTP- oder Oqood-Dokument hoch, um Ihre erste Immobilie automatisch hinzuzufügen und die Wertentwicklung Ihrer Investition zu verfolgen',

    // ── Analysis / DLD market intelligence ────────────────────────────────
    ipNoPortfolioData: 'Keine Portfoliodaten',
    ipDldMarketIntelligence: 'DLD Marktdaten',
    ipPoweredByDld: 'Basierend auf offiziellen Daten des Dubai Land Department',
    ipUpdatedAutomatically: 'Automatisch aktualisiert',
    ipCapitalInvested: 'Investiertes Kapital',
    ipDldMarketValue: 'DLD Marktwert',
    ipBasedOnDldComparables: 'Basierend auf vergleichbaren DLD-Transaktionen',
    ipUnrealizedGain: 'Nicht realisierter Gewinn',
    ipAppreciationLower: 'Wertsteigerung',
    ipDldRentalEstimate: 'DLD Mietschätzung',
    ipAnnualRental: 'Jahresmiete',
    ipPerYear: '/Jahr',
    ipPerMonth: '/Monat',
    ipGrossYield: 'Bruttorendite',
    ipPropertyLevelAnalysis: 'Marktanalyse je Immobilie',
    ipEachPropertyCompared: 'Jede Immobilie im Vergleich mit echten DLD-Transaktionsdaten',
    ipDldDataActive: 'DLD-Daten aktiv',
    ipDldValue: 'DLD Wert',
    ipDldRent: 'DLD Miete',
    ipBookValue: 'Buchwert',
    ipVsPurchase: 'gegenüber Kaufpreis',
    ipComparables: 'Vergleichsobjekte',
    ipDldRentalMarketData: 'DLD Mietmarktdaten',
    ipAvgMonthlyRent: 'Durchschnittliche Monatsmiete',
    ipAvgAnnualRent: 'Durchschnittliche Jahresmiete',
    ipGrossRentalYield: 'Brutto-Mietrendite',
    ipRentalComparables: 'Vergleichbare Mietverträge',
    ipContracts: 'Verträge',
    ipDldPricePerSqm: 'DLD Preis/m²',
    ipTotalRoiCapitalRental: 'Gesamt-ROI (Kapital + Miete)',
    ipCapitalShort: 'Kapital',
    ipRentalShort: 'Miete',
    ipRecentDldTransactions: 'Aktuelle DLD-Transaktionen (vergleichbar)',
    ipRecentDldRentals: 'Aktuelle DLD-Mietverträge (vergleichbar)',
    ipSize: 'Größe',
    ipType: 'Typ',
    ipStartDate: 'Beginn',
    ipAnnualRentCol: 'Jahresmiete',
    ipMonthlyCol: 'Monatlich',
    ipRenewalParen: '(Verlängerung)',
    ipNewParen: '(Neu)',
    ipDldDataAppearsAuto: 'DLD-Marktdaten für diese Immobilie erscheinen automatisch, sobald Transaktionen registriert sind — täglich aktualisiert durch das Dubai Land Department.',
    ipInvestmentSummary: 'Investment-Zusammenfassung',
    ipTotalGain: 'Gesamtgewinn',
    ipRentalYield: 'Mietrendite',
    ipDldDataDisclaimer: 'Die Daten stammen aus den offiziellen Open-Data-Beständen des Dubai Land Department. Die Werte sind Schätzungen auf Basis vergleichbarer Transaktionen.',
    ipHowWeGetData: 'Woher diese Daten kommen',
    ipAllMarketDataSourced: 'Alle Marktdaten stammen aus offiziellen Aufzeichnungen des Dubai Land Department',
    ipDsMarketValue: 'Marktwert',
    ipDsMarketValueDesc: 'Tatsächliche Verkaufspreise vergleichbarer Einheiten im selben Projekt, gewichtet nach Aktualität',
    ipDsPricePerSqft: 'Preis/sqft',
    ipDsPricePerSqftDesc: 'Berechnet aus tatsächlichen DLD-Verkaufsbeträgen geteilt durch die Fläche der Immobilie',
    ipDsRentalPerMonth: 'Miete/Monat',
    ipDsRentalPerMonthDesc: 'Durchschnitt echter, beim DLD registrierter Ejari-Mietverträge für vergleichbare Einheiten',
    ipDsRentalYield: 'Mietrendite',
    ipDsRentalYieldDesc: 'Jährliche DLD-Mieteinnahmen geteilt durch den DLD-Marktwert',
    ipDsComparableSales: 'Vergleichbare Verkäufe',
    ipDsComparableSalesDesc: 'Tatsächlich beim DLD registrierte Verkäufe von Einheiten im selben Projekt und mit ähnlicher Größe',
    ipDsComparableRentals: 'Vergleichbare Mietverträge',
    ipDsComparableRentalsDesc: 'Tatsächlich beim DLD registrierte Ejari-Mietverträge für vergleichbare Immobilien',
    ipSourceLabel: 'Quelle:',
    ipDldApiNote: '— offizielle Open-Data-API des Dubai Land Department. Die Daten werden automatisch alle 6 Stunden aktualisiert. Die Werte sind Schätzungen auf Basis vergleichbarer Transaktionen und können vom tatsächlichen Marktwert abweichen.',
    ipUploadOtpToUnlock: 'Laden Sie OTP-Dokumente hoch, um die DLD Marktdaten freizuschalten — echte Bewertungen, Mietdaten und Vergleichsanalysen auf Basis des Dubai Land Department.',
    ipUploadOtpDocuments: 'OTP-Dokumente hochladen',

    // ── News ──────────────────────────────────────────────────────────────
    ipFailedFetchNews: 'Die Nachrichten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.',
    ipNoRecentNews: 'Keine aktuellen Nachrichten gefunden.',

    // ── Experience / testimonials ─────────────────────────────────────────
    ipInvestorsWhoTrust: 'Investoren, die uns vertrauen',
    ipTestimonial1Title: 'Börsenprofi & Immobilieninvestor',
    ipTestimonial1Quote: 'Mit Michael habe ich einen absoluten Experten an meiner Seite. Und er erzählt nicht nur, sondern investiert auch selbst.',
    ipTestimonial2Title: 'Persönlichkeitstrainer Nr. 1 in Europa',
    ipTestimonial2Quote: 'Die langjährige Erfahrung, die professionelle Abwicklung sowie die herausragende Expertise machen Asset Desk für mich zur Nr. 1 für deutschsprachige Investoren.',
    ipTestimonial3Title: 'Investor | Immobilienunternehmer',
    ipTestimonial3Quote: 'Die Kombination aus Marktdynamik, Momentum und den möglichen An- und Verkaufspreisen in so kurzer Zeit bietet derzeit nur Dubai.',

    // ── Events (congress) ─────────────────────────────────────────────────
    ipKongressSpeakers: 'Referenten',

    // ── Services marketplace / appointments ───────────────────────────────
    ipCompleteAllSteps: 'Bitte führen Sie alle Schritte aus, um einen Termin zu buchen.',
    ipAppointmentBookedPre: 'Termin erfolgreich gebucht für "',
    ipAppointmentBookedOn: '" am',
    ipAppointmentBookedAt: 'um',
    ipMeetingLinkRequested: 'Die Anfrage für den Meeting-Link wurde gesendet. Sie erhalten den Link in Kürze per E-Mail.',
    ipFailedSendRequest: 'Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.',
    ipBooking: 'Wird gebucht...',
    ipConfirmAppointment: 'Termin bestätigen',
    ipSelectDateStep: '3. Datum auswählen',
    ipAtTime: 'um',
    ipDubaiTimeParen: '(Dubai-Zeit)',
    ipCorporateServicesDesc: 'Strategische Beratung für internationale Investoren.',
    ipConveyanceServicesDesc: 'Professionelle Dienstleistungen rund um Immobilientransaktionen.',

    // ── Mietertrag (Start) ────────────────────────────────────────────────
    ipRevenueTrend: 'Mietertrag',
    ipRevenueCollected: 'Miete eingegangen',
    ipRevenueLast12: 'Letzte 12 Monate',
    ipRevenueUpcoming: 'Nächste 12 Monate',
    ipRevenueEmpty: 'Mieteinnahmen erscheinen hier, sobald ein Mietvertrag unterschrieben ist und der erste Scheck fällig wird.',
    ipRevenueFromLeases: 'Aus unterschriebenen Mietverträgen',
    ipRevenueDue: 'fällig',

    // ── Wertentwicklung des Portfolios (Start) ────────────────────────────
    ipViewPortfolio: 'Portfolio ansehen',
    ipValueToday: 'Heute',
    ipValueFirstBuy: 'Erster Kauf',
    ipValueCostNote: 'Stufen bei jedem Kauf; Abschluss zur heutigen DLD-Bewertung.',

    // ── Nächste Auszahlung / Gewinn dieses Jahr (Start) ───────────────────
    ipNextPayout: 'Nächste Auszahlung',
    ipChequeHeld: 'Scheck hinterlegt',
    ipPayoutProgress: '{n} von {total} Mietschecks über alle Mietverhältnisse eingelöst',
    ipPayoutDate: 'Auszahlungsdatum',
    ipPayoutProperty: 'Immobilie',
    ipProfitAllLeases: 'Mietgewinn · alle Mietverträge',
    ipLeaseBasis: 'Miete aus {n} unterzeichneten Mietverträgen, abzüglich laufender Kosten',
    ipNetYieldPa: 'Nettorendite p.a.',
    ipCollected: 'Eingegangen',
    ipOfLeaseRent: 'der Vertragsmiete',
    ipUnitsLet: 'Vermietet',
    ipMonthsN: '{n} Monate',
    ipRentShort: 'Miete',
    ipCostsShort: 'Kosten',
    ipYieldShort: 'Nettorendite',
    ipChequesShort: 'Schecks',
    ipLeasePassed: '{p} % der Laufzeit vergangen',
    ipDaysLeft: 'noch {n} Tage',
    ipLeaseEnded: 'Mietvertrag beendet',
    ipScUnpublished: 'Für dieses Gebäude ist keine Servicegebühr veröffentlicht; der Nettobetrag enthält sie nicht.',
    ipOnPurchasePrice: 'auf den Kaufpreis',
    ipRentLive: 'Verdiente Miete {year} · live',
    ipRentPerDay: '+ AED {day} pro Tag aus {n} unterzeichneten Mietverträgen',
    ipTotalInvested: 'Gesamt investiert',
    ipInvestedSub: '{n} Immobilien · Kaufpreise',
    ipEstValueLive: 'Geschätzter Marktwert · live',
    ipCalcTitle: 'So wird gerechnet',
    ipCalcValue: 'DLD-Bewertung heute, {n} Immobilien',
    ipCalcInvested: 'Gesamt investiert (Kaufpreise)',
    ipCalcGain: 'Differenz',
    ipCalcBasis: 'Wachstum gemessen an {n} Immobilien: bezahlt → DLD-Wert heute',
    ipCalcHeld: 'Durchschnittliche Haltedauer (nach Kaufpreis gewichtet)',
    ipCalcYears: 'Jahre',
    ipCalcRate: 'Jährliche Wachstumsrate',
    ipCalcDay: 'Wachstum pro Tag',
    ipCalcExcluded: 'Nicht in der Wachstumsrate (aber im Wert enthalten): {low} Bewertungen mit geringer Sicherheit und {nodate} Immobilien ohne Kaufdatum. Der Zähler lässt die DLD-Bewertung ab der letzten DLD-Aktualisierung mit dieser Rate wachsen. Es ist eine Schätzung, kein Kaufangebot.',
    ipValueLoading: 'Bewertung des Dubai Land Department wird geladen…',
    ipValueNoRate: 'Noch zu wenig Bewertungshistorie, um eine Wachstumsrate zu schätzen.',
    ipVsInvested: '{gain} ({pct} %) gegenüber Investition',
    ipGrowingEst: 'wächst geschätzt {rate} % p.a., etwa AED {day} pro Tag',
    ipTrendEst: 'Trend über {n} verlässlich bewertete Einheiten: geschätzt {rate} % p.a., etwa AED {day} pro Tag',
    ipValueSource: 'Schätzung aus Transaktionsbewertungen des Dubai Land Department. Wachstumsrate gemessen vom Kaufpreis bis heute an {n} Einheiten mit hoher oder mittlerer Bewertungssicherheit.',
    ipValueSourceDld: 'Jede Einheit bewertet anhand registrierter Verkäufe des Dubai Land Department im selben Gebäude. Aktualisiert, sobald neue Verkäufe registriert werden.',
    ipIncome: 'Mieteinnahmen',
    ipExpenses: 'Laufende Kosten',
  },
};
